import { type Patch } from "immer";

import {
	type TransactionProcessedEvent,
	type TransactionProcessedReplacement,
} from "../api-canvas-metadata/TransactionProcessedEvent";
import {
	type DocumentMetadata,
	makeMessageIdempotentInitMetadataRoomResponseAccepted,
	makeMessageIdempotentInitMetadataRoomResponseRejected,
	makeStepsSinceResponseAccepted,
	makeStepsSinceResponseRejected,
} from "../api-types";
import { ApiCollabResponse } from "../http";
import {
	type InitMetadataResponse,
	type TransactionResponseBody,
} from "../metadata-collab-api-client";

const STANDARD_HEADERS = {
	"Access-Control-Allow-Origin": "*",
	"Access-Control-Allow-Methods": "POST, GET, OPTIONS, DELETE",
	"Access-Control-Allow-Headers": "Content-Type, Authorization",
};

//
// HTTP response types.
//

export const response = <T>(status: number, body: T) =>
	new ApiCollabResponse<T>(body, { status, headers: STANDARD_HEADERS });

export const invalidMetadata = (
	error: string | undefined,
	version: number,
	canvasMetadata: unknown | null
) =>
	response<InitMetadataResponse>(400, {
		error: `invalid document` + error,
		version,
		canvasMetadata,
	});

export const initMetadataRoomSuccessResponse = (
	version: number,
	canvasMetadata: DocumentMetadata
) => response(200, makeMessageIdempotentInitMetadataRoomResponseAccepted(version, canvasMetadata));

export const initMetadataRoomRejectedResponse = (version: number) =>
	response(200, makeMessageIdempotentInitMetadataRoomResponseRejected(version));

export const transactionSuccessResponse = (
	lastAcknowledgedVersion: number,
	lastTransactionProcessed: TransactionProcessedEvent
) => response<TransactionResponseBody>(200, { lastAcknowledgedVersion, lastTransactionProcessed });

export const transactionReplacementResponse = (
	lastAcknowledgedVersion: number,
	lastTransactionProcessed: TransactionProcessedReplacement
) => response<TransactionResponseBody>(200, { lastAcknowledgedVersion, lastTransactionProcessed });

export const transactionRejectedResponse = (
	lastAcknowledgedVersion: number,
	lastTransactionProcessed: TransactionProcessedEvent
) => response<TransactionResponseBody>(202, { lastAcknowledgedVersion, lastTransactionProcessed });

export const transactionRejectedDueToFailedPersistenceResponse = (
	lastAcknowledgedVersion: number,
	lastTransactionProcessed: TransactionProcessedEvent
) => response<TransactionResponseBody>(503, { lastAcknowledgedVersion, lastTransactionProcessed });

export const stepsSinceAcceptedResponse = (version: number, patches: Patch[]) =>
	response(200, makeStepsSinceResponseAccepted(version, patches));

export const stepsSinceRejectedResponse = (error: string) =>
	response(200, makeStepsSinceResponseRejected(error));
